import React, { FC, useState } from 'react';
import {
  Avatar,
  Box,
  Checkbox,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Company } from '@staffedupusa/shared-components';
import {
  lightGrey2,
  primaryGreen,
  primaryGrey,
  primaryRed,
  white,
  yellow,
} from '@styles/colors';
import CompanyPaymentDetailModal from './CompanyPaymentDetailModal';
import { PLANS } from '@utils/pricing';

type ExtendedApplication = Company & {
  is_company_paid: boolean;
  sub_users?: { length: number }[];
  subscription?: {
    date_created: string;
    date_updated: string;
    id: number;
    stripe_id: string;
    billing_cycle?: string;
    next_billing_date?: string;
    total_amount?: number;
  };
};

type CompaniesListWithPaymentProps = {
  company: ExtendedApplication;
  totalAggregate: any;
  selectedCompanies: number[];
  handleSelectCompany: (companyId: number) => void;
};

const CompaniesListWithPayment: FC<CompaniesListWithPaymentProps> = ({
  company,
  selectedCompanies,
  totalAggregate,
  handleSelectCompany,
}) => {
  const [openModal, setOpenModal] = useState(false);

  const getLabel = (company: Company) => {
    switch (company.plan) {
      case 0:
        return 'Expired';
      case 999:
        return 'Unpaid';
      case 1:
      case 2:
      case 4:
        return 'Paid';
      case 3:
        return 'Paid';
      default:
        return '-';
    }
  };

  const getBackgroundColor = (company: Company) => {
    switch (company?.plan) {
      case 0:
        return primaryRed;
      case 999:
        return yellow;
      case 1:
      case 2:
      case 4:
        return primaryGreen;
      default:
        return primaryGrey;
    }
  };

  const getPlanDetail = (plan: number) => {
    switch (plan) {
      case 0:
        return PLANS.EXPIRED.NAME;
      case 1:
        return PLANS.SMARTER.NAME;
      case 2:
        return PLANS.PRO.NAME;
      case 4:
        return PLANS.STARTER.NAME;
      case 999:
        return PLANS.TRIAL.NAME;
      default:
        return '-';
    }
  };

  return (
    <>
      <TableRow
        sx={{ cursor: 'pointer' }}
        hover
        onClick={(e) => {
          e.stopPropagation();
          setOpenModal(true);
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={selectedCompanies.includes(company.id)}
            onChange={(e) => {
              handleSelectCompany(company.id);
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        </TableCell>
        <TableCell>
          <Stack direction="row" alignItems="center">
            <Avatar
              src={(company.s_logo_file as string) || ''}
              style={{
                width: '50px',
                height: '50px',
                marginRight: '12px',
                borderRadius: 5,
              }}
            />
            <Stack>
              <Typography variant="h6" gutterBottom>
                {company.s_name}
              </Typography>
              {company?.is_company_paid && company.plan !== 0 && (
                <Typography
                  textTransform="capitalize"
                  variant="subtitle2"
                  color={lightGrey2}
                  gutterBottom
                >
                  {company.subscription?.billing_cycle
                    ? `${company.subscription.billing_cycle}ly`
                    : ''}
                  {company.subscription?.next_billing_date ? (
                    <>
                      {' '}
                      (Next invoice:{' '}
                      <b>{company.subscription.next_billing_date}</b>)
                    </>
                  ) : (
                    ''
                  )}
                </Typography>
              )}
            </Stack>
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="h6" gutterBottom>
            {getPlanDetail(company.plan)}
            <br />
          </Typography>
          {company?.sub_users && company?.sub_users?.length > 0 && (
            <Typography variant="subtitle2" color={lightGrey2} gutterBottom>
              {company.sub_users?.length} Sub users
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="h6" gutterBottom>
            <Typography variant="h6" gutterBottom>
              {company?.is_company_paid
                ? company.subscription?.total_amount &&
                  company.subscription.total_amount > 0
                  ? `$${company.subscription.total_amount}`
                  : 0
                : totalAggregate
                  ? `$${totalAggregate?.total_amount}`
                  : 0}
            </Typography>
          </Typography>
        </TableCell>
        <TableCell>
          <Box
            sx={{
              backgroundColor: getBackgroundColor(company),
              color: white,
              padding: 0.5,
              textAlign: 'center',
              borderRadius: 5,
            }}
          >
            {getLabel(company)}
          </Box>
        </TableCell>
      </TableRow>
      {openModal ? (
        <CompanyPaymentDetailModal
          openModal={openModal}
          getPlanDetail={getPlanDetail}
          getLabel={getLabel}
          getBackgroundColor={getBackgroundColor}
          handleClose={() => setOpenModal(false)}
          company={company}
          totalAggregate={totalAggregate}
        />
      ) : null}
    </>
  );
};

export default CompaniesListWithPayment;
